import React, { useState, useEffect } from "react";
import { X, Plus, Mail, FileSpreadsheet, FileText, Files } from "lucide-react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { useGenerateReport, useGetUserDevices, useGetAllLocations } from "../../queries/admin";
import { toast } from "react-toastify";
import { format } from "date-fns";
import DatePicker from "../../pages/new/DatePicker";
import { HStack } from "../../components/component/utils";
import { Icon } from "@iconify/react";
import { useAuthStore } from "../../store/useAuthStore";
import { IWeatherData } from "../../types/device";

// Export the metricDataMapping for use in other components
export const metricDataMapping: { [key: string]: keyof IWeatherData } = {
  Temperature: "temperature",
  Humidity: "humidity",
  Pressure: "pressure",
  "Carbon-Dioxide": "co2",
  VOCs: "vocs",
  Light: "light",
  Noise: "noise",
  PM1: "pm1",
  "PM2.5": "pm25",
  PM4: "pm4",
  PM10: "pm10",
  AIQ: "aiq",
  "Gas-1": "gas1",
  "Gas-2": "gas2",
  "Gas-3": "gas3",
  "Gas-4": "gas4",
  "Gas-5": "gas5",
  "Gas-6": "gas6",
};

// Available emails section removed - now users add their own emails

interface ReportDialogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ReportDialogModal({ isOpen, onClose }: ReportDialogModalProps) {
  const { mutate: generateReport, isPending } = useGenerateReport();
  const { data: userData } = useGetUserDevices();
  const { data: locations } = useGetAllLocations();

  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("all");
  const [selectedFromDate, setSelectedFromDate] = useState<string>("");
  const [selectedToDate, setSelectedToDate] = useState<string>("");
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState<string>("");
  const [reportFormat, setReportFormat] = useState<"excel" | "pdf" | "both">("both");

  const locationFilteredDevices =
    userData?.devices?.filter(
      (device: any) => !selectedLocationId || device.locationId === selectedLocationId,
    ) ?? [];

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
    setSelectedDeviceId("all");
  };

  // Automatically add current user's email when modal opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen && userData?.email && !selectedEmails.includes(userData.email)) {
      setSelectedEmails([userData.email]);
    }
  }, [isOpen, userData?.email]);

  const handleEmailSelect = (email: string) => {
    if (!selectedEmails.includes(email)) {
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const handleEmailRemove = (email: string) => {
    setSelectedEmails(selectedEmails.filter((e) => e !== email));
  };

  const handleAddNewEmail = () => {
    if (newEmail && !selectedEmails.includes(newEmail) && isValidEmail(newEmail)) {
      setSelectedEmails([...selectedEmails, newEmail]);
      setNewEmail("");
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleGenerateReport = () => {
    if (
      selectedFromDate &&
      selectedToDate &&
      new Date(selectedFromDate) > new Date(selectedToDate)
    ) {
      toast.error("'From' date must be before 'To' date");
      return;
    }
    if (selectedDeviceId && selectedEmails.length > 0 && selectedFromDate && selectedToDate) {
      generateReport(
        {
          deviceId: selectedDeviceId,
          locationId:
            selectedDeviceId === "all" && selectedLocationId ? selectedLocationId : undefined,
          from: format(new Date(selectedFromDate), "yyyy-MM-dd"),
          to: format(new Date(selectedToDate), "yyyy-MM-dd"),
          email: selectedEmails.join(","),
          format: reportFormat,
        },
        {
          onSuccess() {
            toast(`Report will be sent to ${selectedEmails.length} recipient(s)`, {
              type: "success",
              autoClose: 3000,
            });
            onClose();
            // Reset form
            setSelectedLocationId("");
            setSelectedDeviceId("all");
            setSelectedFromDate("");
            setSelectedToDate("");
            setSelectedEmails([]);
            setNewEmail("");
            setReportFormat("both");
          },
          onError() {
            toast("Failed to generate report. Please try again.", {
              type: "error",
              autoClose: 2000,
            });
          },
        },
      );
    }
  };

  return (
    <Transition show={isOpen}>
      <Dialog className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-sm transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                <div className="flex flex-col justify-between bg-white rounded-xl max-h-[90vh] overflow-y-auto">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">Generate Report</h2>
                          <p className="text-sm text-gray-500">
                            Create and download sensor data reports
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-6">
                    {/* Location & Device Selection */}
                    <div className="w-full flex flex-row gap-6 mb-6">
                      <div className="w-full">
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Select Location
                        </label>
                        <div className="relative">
                          <select
                            value={selectedLocationId}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            className="w-full h-10 px-3 py-1.5 pr-10 border-0 rounded-lg bg-white appearance-none ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 dark:text-gray-200 text-sm sm:leading-6 cursor-pointer"
                          >
                            <option value="">All Locations</option>
                            {locations?.map((location: any) => (
                              <option key={location.id} value={location.id}>
                                {location.name}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg
                              className="h-4 w-4 text-gray-500"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Select Device
                        </label>
                        <div className="relative">
                          <select
                            value={selectedDeviceId}
                            onChange={(e) => setSelectedDeviceId(e.target.value)}
                            className="w-full h-10 px-3 py-1.5 pr-10 border-0 rounded-lg bg-white appearance-none ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 dark:text-gray-200 text-sm sm:leading-6 cursor-pointer"
                          >
                            <option value="all">
                              All Devices ({locationFilteredDevices.length})
                            </option>
                            {locationFilteredDevices.map((device: any) => (
                              <option key={device.id} value={device.id}>
                                {device.name}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg
                              className="h-4 w-4 text-gray-500"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        {selectedDeviceId === "all" && (
                          <p className="text-xs text-gray-500 mt-1">
                            A separate report will be generated for each device
                            {selectedLocationId ? " in the selected location" : ""}.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Date Range Selection */}
                    <div className="w-full flex flex-row gap-6 mb-8">
                      <div className="w-full">
                        <DatePicker
                          label="From Date"
                          value={selectedFromDate}
                          onChange={setSelectedFromDate}
                        />
                      </div>
                      <div className="w-full">
                        <DatePicker
                          label="To Date"
                          value={selectedToDate}
                          onChange={setSelectedToDate}
                        />
                      </div>
                    </div>

                    {/* Report Format Selection */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Format</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setReportFormat("excel")}
                          className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all duration-200 ${
                            reportFormat === "excel"
                              ? "border-green-500 bg-green-50 shadow-sm"
                              : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <FileSpreadsheet
                            className={`h-6 w-6 ${reportFormat === "excel" ? "text-green-600" : "text-gray-400"}`}
                          />
                          <span
                            className={`text-sm font-medium ${reportFormat === "excel" ? "text-green-700" : "text-gray-600"}`}
                          >
                            Excel Only
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setReportFormat("pdf")}
                          className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all duration-200 ${
                            reportFormat === "pdf"
                              ? "border-red-500 bg-red-50 shadow-sm"
                              : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <FileText
                            className={`h-6 w-6 ${reportFormat === "pdf" ? "text-red-600" : "text-gray-400"}`}
                          />
                          <span
                            className={`text-sm font-medium ${reportFormat === "pdf" ? "text-red-700" : "text-gray-600"}`}
                          >
                            PDF Only
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setReportFormat("both")}
                          className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all duration-200 ${
                            reportFormat === "both"
                              ? "border-blue-500 bg-blue-50 shadow-sm"
                              : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <Files
                            className={`h-6 w-6 ${reportFormat === "both" ? "text-blue-600" : "text-gray-400"}`}
                          />
                          <span
                            className={`text-sm font-medium ${reportFormat === "both" ? "text-blue-700" : "text-gray-600"}`}
                          >
                            Both
                          </span>
                        </button>
                      </div>
                      {reportFormat === "pdf" || reportFormat === "both" ? (
                        <p className="text-xs text-gray-500 mt-2">
                          PDF includes device, location & organisation details with parameter
                          averages.
                        </p>
                      ) : null}
                    </div>

                    {/* Email Selection Section */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Recipients</h3>

                      {/* Add New Email */}
                      <div className="mb-4">
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Add Additional Recipients
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter email address..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleAddNewEmail();
                              }
                            }}
                          />
                          <button
                            onClick={handleAddNewEmail}
                            disabled={
                              !newEmail ||
                              !isValidEmail(newEmail) ||
                              selectedEmails.includes(newEmail)
                            }
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center min-w-[44px]"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        {newEmail && !isValidEmail(newEmail) && (
                          <p className="text-red-500 text-xs mt-1">
                            Please enter a valid email address
                          </p>
                        )}
                      </div>

                      {/* Selected Emails */}
                      <div className="mb-6">
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Selected Recipients ({selectedEmails.length})
                        </label>
                        <div className="bg-gray-50 rounded-lg p-4 min-h-[60px]">
                          {selectedEmails.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {selectedEmails.map((email) => (
                                <span
                                  key={email}
                                  className="inline-flex items-center bg-blue-500 text-white rounded-full px-3 py-1 text-sm font-semibold transition-all hover:bg-blue-600"
                                >
                                  {email}
                                  <button
                                    onClick={() => handleEmailRemove(email)}
                                    className="cursor-pointer ml-2 focus:outline-none hover:text-red-200"
                                  >
                                    <X size={14} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm italic flex items-center justify-center h-8">
                              No recipients selected. Add your email address above.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Generate Report Button */}
                    <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-900 font-medium">
                            Ready to generate report for {selectedEmails.length} recipient
                            {selectedEmails.length !== 1 ? "s" : ""}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-xs text-gray-900">Selected</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg font-medium transition-all duration-200"
                          >
                            Cancel
                          </button>
                          <button
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                              selectedDeviceId &&
                              !(selectedDeviceId === "all" && locationFilteredDevices.length === 0) &&
                              selectedEmails.length > 0 &&
                              selectedFromDate &&
                              selectedToDate &&
                              !isPending
                                ? "bg-primary hover:bg-primary/90 text-white shadow-sm hover:shadow-md"
                                : "bg-gray-200 text-gray-500 cursor-not-allowed"
                            }`}
                            disabled={
                              !selectedDeviceId ||
                              (selectedDeviceId === "all" &&
                                locationFilteredDevices.length === 0) ||
                              selectedEmails.length === 0 ||
                              !selectedFromDate ||
                              !selectedToDate ||
                              isPending
                            }
                            onClick={handleGenerateReport}
                          >
                            <Icon
                              icon={isPending ? "mdi:loading" : "lucide:download"}
                              className={`w-4 h-4 ${isPending ? "animate-spin" : ""}`}
                            />
                            <span>{isPending ? "Generating..." : "Generate Report"}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
